import LegalDocument, { type LegalSection } from "@/components/feature/LegalDocument";
import { SITE } from "@/config/site";

const sections: LegalSection[] = [
  {
    id: "scope",
    title: "1. Scope of this policy",
    body: [
      `This Refund Policy applies to fees paid to ${SITE.name} for paid listing plans and any other paid features offered on the platform. It should be read together with our Terms of Service.`,
      "This policy is intended to comply with the Consumer Protection Act, 2019 and the rules made thereunder, and does not affect any statutory rights you may have under Indian law.",
    ],
  },
  {
    id: "plans",
    title: "2. Listing plans and billing",
    body: [
      "Paid plans are billed for the period you select at checkout — monthly, quarterly or annually. The applicable fee, billing cycle and features are shown on the Listing Plans page and in your invoice.",
      "Unless otherwise stated, prices are exclusive of GST and other applicable taxes, which are added at checkout where applicable. A tax invoice is issued for each successful payment.",
      "Paid plans renew automatically only if you have enabled auto-renewal; otherwise they lapse at the end of the paid period.",
    ],
  },
  {
    id: "eligible",
    title: "3. When a refund may be available",
    body: [
      "Duplicate or failed transactions: if you are charged twice for the same purchase, or a payment is deducted but the plan is not activated, the excess or mistaken amount will be refunded.",
      "Service not delivered: if a paid feature you purchased cannot be provided due to a fault attributable to us and we are unable to resolve it, you may be eligible for a pro-rata refund for the unused period.",
      "Cooling-off: where a plan is newly purchased, you may request a refund within 7 days of purchase, provided the plan has not been materially used — for example, where no listing has been published or no premium feature (such as a verified badge or priority placement) has been activated.",
    ],
  },
  {
    id: "not-eligible",
    title: "4. When a refund is not available",
    body: [
      "Fees for a billing period that has already started are generally non-refundable, except as required by law or as stated in this policy.",
      "Refunds are not available where a listing was removed or suspended because it breached our Terms of Service, or where the issue arises from information you provided that was inaccurate or incomplete.",
      "Refunds are not available for change of mind after the cooling-off period, or for results such as enquiries, leads or rankings, which depend on factors outside our control.",
    ],
  },
  {
    id: "process",
    title: "5. How to request a refund",
    body: [
      "To request a refund, email our Grievance Officer with your invoice number, account email, transaction reference and the reason for the request. Please include relevant screenshots where applicable.",
      "We will acknowledge your request within 48 hours and complete our review within 7 working days of receiving all required information.",
    ],
  },
  {
    id: "processing",
    title: "6. Processing of approved refunds",
    body: [
      "Approved refunds are credited to the original payment method within 7 to 10 working days from approval, subject to the timelines of your bank or payment provider.",
      "Refunds are issued in Indian Rupees (INR) for the amount actually received, net of any non-refundable third-party payment gateway charges where applicable and permitted by law.",
      "Where permitted and agreed with you, we may, at our discretion, offer a credit note or adjustment against a future plan instead of a cash refund.",
    ],
  },
  {
    id: "cancellation",
    title: "7. Cancellation of plans",
    body: [
      "You may cancel a paid plan at any time from your account or by contacting us. Cancellation stops future renewals; it does not automatically entitle you to a refund for the current period, except as provided in this policy.",
      "After cancellation, the plan remains active until the end of the paid period, following which the listing reverts to the free tier, subject to directory guidelines.",
    ],
  },
  {
    id: "disputes",
    title: "8. Chargebacks and disputes",
    body: [
      "If you believe a charge is incorrect, please contact us first so we can resolve the matter. Raising a chargeback without contacting us may result in suspension of the account while the dispute is investigated.",
    ],
  },
  {
    id: "law",
    title: "9. Governing law and jurisdiction",
    body: [
      "This Refund Policy is governed by the laws of India. Any dispute arising out of or in connection with payments or refunds is subject to the exclusive jurisdiction of the courts at Mysore, Karnataka, India.",
    ],
  },
];

export default function RefundPolicy() {
  return (
    <LegalDocument
      crumb="Refund Policy"
      eyebrow="Legal"
      icon="ri-refund-2-line"
      heading="Refund Policy"
      subtitle={`How refunds, cancellations and billing work for paid plans and features purchased on ${SITE.name}.`}
      updated="October 2026"
      seoTitle="Refund Policy"
      seoDescription={`Read the refund, cancellation and billing policy for paid listing plans and features on ${SITE.name}, under Indian consumer protection law.`}
      seoKeywords="Mysore Yellow Pages refund policy, listing plan refund, cancellation policy Karnataka"
      path="/refund-policy"
      sections={sections}
    />
  );
}